import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { getNodeApiBase, getPhpBaseUrl } from "../utils/apiBase";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const VerifyEmail = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const [email, setEmail] = useState(query.get("email") || "");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const hasPending = (() => {
    try { return !!sessionStorage.getItem('pendingSignup'); } catch (_) { return false; }
  })();
  const emailLocked = !!query.get("email") || hasPending;

  const apiBase = getNodeApiBase();
  const phpBase = getPhpBaseUrl();

  const normalizeCode = (v) => v.replace(/[^0-9]/g, "").slice(0, 4);

  const handleSend = async () => {
    setError("");
    setMessage("");
    if (!email) {
      setError("Veuillez saisir votre e-mail.");
      return;
    }
    setSending(true);
    try {
      // Backend should send a 4-digit code to the user's email
      await axios.post(`${apiBase}/api/auth/send-verification-code`, { email });
      setMessage("Un code de vérification a été envoyé à votre e-mail.");
    } catch (e) {
      const serverMsg = e?.response?.data?.message;
      setError(serverMsg || "L'envoi du code a échoué. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    const c = normalizeCode(code);
    if (!email) {
      setError("E-mail requis.");
      return;
    }
    if (c.length !== 4) {
      setError("Le code doit contenir 4 chiffres.");
      return;
    }
    setVerifying(true);
    try {
      // Backend should verify the code and mark the account as active
      const res = await axios.post(`${apiBase}/api/auth/verify-code`, { email, code: c });
      if (res?.data?.success) {
        // If we have pending signup data, complete the actual signup now
        try {
          const pendingRaw = sessionStorage.getItem('pendingSignup');
          if (pendingRaw) {
            const pending = JSON.parse(pendingRaw);
            if (pending && pending.email && pending.password) {
              await axios.post(`${phpBase}/signup.php`, {
                Nom: pending.Nom,
                telephone: pending.telephone,
                password: pending.password,
                email: pending.email,
                prenom: pending.prenom,
              });
              // Send a welcome email after account creation
              try {
                const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin + "/login";
                await axios.post(`${apiBase}/api/auth/send-welcome`, {
                  email: pending.email,
                  loginUrl: frontendUrl,
                });
              } catch (e) {
                console.warn('Welcome email failed:', e?.message || e);
              }
            }
          }
        } catch (e) {
          // If signup fails here, keep the user informed but don't block verification status
          console.warn('Post-verify signup failed:', e?.message || e);
        } finally {
          try { sessionStorage.removeItem('pendingSignup'); } catch (_) {}
        }

        setMessage("Votre e-mail a été vérifié avec succès. Vous pouvez maintenant vous connecter.");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(res?.data?.message || "Code invalide.");
      }
    } catch (e) {
      const serverMsg = e?.response?.data?.message;
      setError(serverMsg || "La vérification a échoué. Réessayez.");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    // Auto-send code only if we don't already have a pending signup (to avoid overwriting the code)
    const pending = sessionStorage.getItem('pendingSignup');
    if (query.get("email") && !pending) {
      handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#e4d2d2",
        padding: 20,
        width: "100vw",
        height: "100vh",
      }}
    >
      <div style={{ background: "#fff", border: "1px solid #e6e8eb", borderRadius: 12, boxShadow: "0 8px 16px rgba(16,24,40,0.08)", width: "100%", maxWidth: 420, padding: 24 }}>
        <h2 style={{ marginTop: 0, marginBottom: 8, textAlign: "center" }}>Vérification de l'e-mail</h2>
        <p style={{ marginTop: 0, color: "#4b5563", textAlign: "center" }}>
          Saisissez le code de 4 chiffres envoyé à votre e-mail.
        </p>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>E-mail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="exemple@domaine.com"
            readOnly={emailLocked}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d0d5dd", background: emailLocked ? "#f3f4f6" : "#fff" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Code (4 chiffres)</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={(e) => setCode(normalizeCode(e.target.value))}
            placeholder="0000"
            maxLength={4}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d0d5dd", letterSpacing: 6, textAlign: "center", fontWeight: 700 }}
          />
        </div>

        {error && <div style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>}
        {message && <div style={{ color: "#065f46", marginBottom: 12 }}>{message}</div>}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{ flex: 1, padding: 12, background: sending ? "#9ca3af" : "#0ea5e9", color: "#fff", border: 0, borderRadius: 8, fontWeight: 700, cursor: sending ? "not-allowed" : "pointer" }}
          >
            {sending ? "Envoi..." : "Renvoyer le code"}
          </button>
          <button
            onClick={handleVerify}
            disabled={verifying}
            style={{ flex: 1, padding: 12, background: verifying ? "#9ca3af" : "#0b7a3e", color: "#fff", border: 0, borderRadius: 8, fontWeight: 700, cursor: verifying ? "not-allowed" : "pointer" }}
          >
            {verifying ? "Vérification..." : "Vérifier"}
          </button>
        </div>

        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button onClick={() => navigate("/login")} style={{ background: "transparent", color: "#0b7a3e", border: 0, cursor: "pointer", fontWeight: 600 }}>Retour à la connexion</button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
